import React, { useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home as HomeIcon,
  Building2,
  Triangle,
  Square,
  Mountain,
  Zap,
  BatteryCharging,
  Plug,
  Clock,
  ArrowRight,
  ArrowLeft,
  Check,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const WEB3FORMS_ENDPOINT = "https://api.web3forms.com/submit";
const WEB3FORMS_ACCESS_KEY = "8a0b6ca3-3948-40c3-8616-39064fdb29e0";

const stepsConfig = [
  {
    key: "hauseigentum",
    title: "Sind Sie Eigentümer der Immobilie?",
    sub: "Wir benötigen das Einverständnis des Eigentümers für eine PV-Installation.",
    options: [
      { value: "ja", label: "Ja, ich bin Eigentümer", icon: HomeIcon },
      { value: "nein", label: "Nein, Mieter", icon: Building2 },
    ],
    cols: 2,
  },
  {
    key: "dachform",
    title: "Welche Dachform hat Ihr Haus?",
    sub: "Die Dachform beeinflusst die optimale Modulplatzierung.",
    options: [
      { value: "satteldach", label: "Satteldach", icon: Triangle },
      { value: "flachdach", label: "Flachdach", icon: Square },
      { value: "walmdach", label: "Walmdach", icon: Mountain },
      { value: "pultdach", label: "Pultdach", icon: Triangle },
    ],
    cols: 2,
  },
  {
    key: "stromverbrauch",
    title: "Wie hoch ist Ihr jährlicher Stromverbrauch?",
    sub: "Schauen Sie auf Ihre letzte Stromrechnung – oder schätzen Sie.",
    options: [
      { value: "bis-3000", label: "Bis 3'000 kWh" },
      { value: "3000-5000", label: "3'000 – 5'000 kWh" },
      { value: "5000-8000", label: "5'000 – 8'000 kWh" },
      { value: ">8000", label: "Mehr als 8'000 kWh" },
    ],
    cols: 2,
  },
  {
    key: "batteriespeicher",
    title: "Interesse an einem Batteriespeicher?",
    sub: "Damit nutzen Sie Ihren Solarstrom auch nachts.",
    options: [
      { value: "ja", label: "Ja, unbedingt", icon: BatteryCharging },
      { value: "vielleicht", label: "Vielleicht – beraten Sie mich", icon: Sparkles },
      { value: "nein", label: "Nein, vorerst nicht", icon: Zap },
    ],
    cols: 3,
  },
  {
    key: "wallbox",
    title: "Interesse an einer Wallbox?",
    sub: "Sonnenstrom direkt ins Elektroauto laden.",
    options: [
      { value: "ja", label: "Ja, ich habe / plane ein E-Auto", icon: Plug },
      { value: "vielleicht", label: "Vielleicht in den nächsten Jahren", icon: Sparkles },
      { value: "nein", label: "Nein, nicht relevant", icon: Zap },
    ],
    cols: 3,
  },
  {
    key: "zeitrahmen",
    title: "Wann möchten Sie die Anlage realisieren?",
    sub: "Ein realistischer Zeitrahmen hilft uns bei der Planung.",
    options: [
      { value: "sofort", label: "So schnell wie möglich", icon: Clock },
      { value: "3monate", label: "In den nächsten 3 Monaten", icon: Clock },
      { value: "6monate", label: "In den nächsten 6 Monaten", icon: Clock },
      { value: "unklar", label: "Unklar – ich sammle Infos", icon: Clock },
    ],
    cols: 2,
  },
];

export default function LeadFunnel() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({
    name: "",
    email: "",
    phone: "",
    plz: "",
    ort: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const totalSteps = stepsConfig.length + 2; // +estimate +contact
  const progress = useMemo(
    () => Math.min(100, Math.round(((step + 1) / (totalSteps + (done ? 1 : 0))) * 100)),
    [step, totalSteps, done]
  );

  // -------- Solar-Check Schätzung (grobe Richtwerte für CH) --------
  const estimate = useMemo(() => {
    const consumptionMap = {
      "bis-3000": 2500,
      "3000-5000": 4000,
      "5000-8000": 6500,
      ">8000": 9500,
    };
    const consumption = consumptionMap[answers.stromverbrauch] || 4000;

    // Anlagengrösse: ca. das 1.6-fache des Jahresverbrauchs in kWp, gerundet auf halbe kWp
    let kwp = Math.round((consumption / 950) * 1.6 * 2) / 2;
    if (kwp < 4) kwp = 4;
    if (kwp > 30) kwp = 30;

    // Ertrag pro kWp in CH ~950 kWh/a (Dachform leichter Modifier)
    const roofYield = {
      satteldach: 980,
      pultdach: 950,
      walmdach: 900,
      flachdach: 870,
    };
    const yieldPerKwp = roofYield[answers.dachform] || 950;
    const yearlyYield = Math.round(kwp * yieldPerKwp);

    // Eigenverbrauchsquote
    let selfUse = 0.30;
    if (answers.batteriespeicher === "ja") selfUse += 0.30;
    if (answers.batteriespeicher === "vielleicht") selfUse += 0.10;
    if (answers.wallbox === "ja") selfUse += 0.10;
    selfUse = Math.min(selfUse, 0.80);

    const priceBuy = 0.32;   // CHF/kWh Endkundenpreis
    const priceFeedIn = 0.10; // CHF/kWh Rückspeisetarif

    const usedAtHome = yearlyYield * selfUse;
    const sold = yearlyYield * (1 - selfUse);
    const savings = Math.round(usedAtHome * priceBuy + sold * priceFeedIn);

    // Investitionskosten (Richtwerte CH)
    let invest = kwp * 1900;
    if (answers.batteriespeicher === "ja") invest += 9500;
    if (answers.wallbox === "ja") invest += 2400;
    invest = Math.round(invest);

    const payback = savings > 0 ? Math.round((invest / savings) * 10) / 10 : null;

    return { kwp, yearlyYield, savings, invest, payback, selfUsePct: Math.round(selfUse * 100) };
  }, [answers]);

  const select = (key, value) => {
    setAnswers((a) => ({ ...a, [key]: value }));
    // small delay so the user sees their selection before moving on
    setTimeout(() => {
      setStep((s) => Math.min(s + 1, stepsConfig.length));
    }, 220);
  };

  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async (e) => {
    e.preventDefault();
    if (!contact.name || !contact.email) {
      toast.error("Bitte Name und E-Mail ausfüllen.");
      return;
    }
    setSubmitting(true);
    try {
      const message = [
        "Neuer Solar-Check Lead von solanova.ch",
        "",
        "Kontakt",
        `Name: ${contact.name}`,
        `E-Mail: ${contact.email}`,
        `Telefon: ${contact.phone || "-"}`,
        `PLZ / Ort: ${[contact.plz, contact.ort].filter(Boolean).join(" ") || "-"}`,
        "",
        "Angaben aus dem Solar-Check",
        `Hauseigentum: ${answers.hauseigentum || "-"}`,
        `Dachform: ${answers.dachform || "-"}`,
        `Stromverbrauch: ${answers.stromverbrauch || "-"}`,
        `Batteriespeicher: ${answers.batteriespeicher || "-"}`,
        `Wallbox: ${answers.wallbox || "-"}`,
        `Zeitrahmen: ${answers.zeitrahmen || "-"}`,
        "",
        "Berechnete Schaetzung",
        `Anlagengroesse: ${estimate.kwp} kWp`,
        `Jahresertrag: ${estimate.yearlyYield} kWh`,
        `Jahresersparnis: CHF ${estimate.savings}`,
        `Investition: CHF ${estimate.invest}`,
        `Amortisation: ${estimate.payback} Jahre`,
      ].join("\n");

      const { data } = await axios.post(WEB3FORMS_ENDPOINT, {
        access_key: WEB3FORMS_ACCESS_KEY,
        subject: `Neuer Solar-Check Lead - ${contact.name}`,
        from_name: "Solanova Website",
        name: contact.name,
        email: contact.email,
        phone: contact.phone || "-",
        plz: contact.plz || "-",
        ort: contact.ort || "-",
        message,
      });

      if (!data?.success) {
        throw new Error(data?.message || "Web3Forms submission failed");
      }

      setDone(true);
      toast.success("Anfrage erfolgreich übermittelt. Wir melden uns innert 24 h.");
    } catch (err) {
      console.error(err);
      toast.error("Senden fehlgeschlagen. Bitte versuchen Sie es nochmals.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setAnswers({});
    setContact({ name: "", email: "", phone: "", plz: "", ort: "" });
    setStep(0);
    setDone(false);
  };

  const isEstimateStep = step === stepsConfig.length;
  const isContactStep = step === stepsConfig.length + 1;
  const currentStep = stepsConfig[step];

  return (
    <section
      id="solar-check"
      data-testid="funnel-section"
      className="section relative bg-[var(--sol-bg)] border-t hairline"
    >
      {/* bronze glow */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-[400px] pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at top, rgba(200,162,122,0.10), rgba(7,7,7,0))",
        }}
      />

      <div className="relative max-w-4xl mx-auto px-6 md:px-10">
        <div className="text-center">
          <p className="eyebrow">Solar-Check</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-extralight tracking-tight mt-6 text-white leading-[1.05]">
            In 60 Sekunden zur
            <br />
            <span className="bronze-metallic">persönlichen Solar-Analyse.</span>
          </h2>
          <p className="font-body text-white/55 mt-6 max-w-xl mx-auto text-base md:text-lg leading-relaxed">
            Beantworten Sie sechs einfache Fragen und erhalten Sie sofort
            eine unverbindliche Einschätzung von Anlagengrösse, Ertrag und Ersparnis.
          </p>
        </div>

        <div
          data-testid="funnel-container"
          className="mt-14 bg-[var(--sol-surface)] border border-white/10 rounded-3xl p-6 md:p-12 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] relative overflow-hidden"
        >
          {/* progress */}
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] tracking-[0.22em] uppercase text-white/40 font-body">
              {done
                ? "Abgeschlossen"
                : `Schritt ${step + 1} / ${totalSteps}`}
            </span>
            <span
              className="text-[11px] tracking-[0.22em] uppercase font-body text-[var(--sol-bronze)]"
              data-testid="funnel-progress-label"
            >
              {progress}%
            </span>
          </div>
          <div className="h-[3px] bg-white/8 rounded-full overflow-hidden">
            <div
              data-testid="funnel-progress-bar"
              className="h-full bg-[var(--sol-bronze)] transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* step body */}
          <div className="mt-10 min-h-[360px] relative">
            <AnimatePresence mode="wait">
              {done ? (
                <motion.div
                  key="done"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.5 }}
                  data-testid="funnel-success"
                  className="text-center py-10"
                >
                  <div className="w-16 h-16 rounded-full bg-[var(--sol-bronze)]/15 border border-[var(--sol-bronze)]/40 flex items-center justify-center mx-auto">
                    <Check size={26} className="text-[var(--sol-bronze-light)]" />
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl text-white font-light mt-8">
                    Vielen Dank, {contact.name.split(" ")[0] || "Sie"}.
                  </h3>
                  <p className="font-body text-white/60 mt-4 max-w-md mx-auto leading-relaxed">
                    Ihre Anfrage ist bei uns eingegangen. Unser Solar-Experte meldet sich
                    innert 24 Stunden persönlich bei Ihnen.
                  </p>
                  <button
                    onClick={reset}
                    data-testid="funnel-reset"
                    className="mt-10 btn-ghost rounded-full px-7 py-3 text-sm font-medium"
                  >
                    Neuen Check starten
                  </button>
                </motion.div>
              ) : isEstimateStep ? (
                <motion.div
                  key="estimate-step"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.45 }}
                  data-testid="funnel-estimate-step"
                >
                  <div className="text-center">
                    <p className="eyebrow">Ihre persönliche Schätzung</p>
                    <h3 className="font-display text-2xl md:text-4xl text-white font-light leading-tight mt-4">
                      Auf Basis Ihrer Angaben empfehlen wir:
                    </h3>
                    <p className="font-body text-white/50 mt-3 text-sm md:text-base max-w-xl mx-auto">
                      Unverbindliche Richtwerte. Die finale Auslegung erfolgt nach
                      individueller Dach­analyse durch unser Team.
                    </p>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mt-10">
                    {[
                      {
                        id: "kwp",
                        label: "Anlagengrösse",
                        value: `${estimate.kwp.toLocaleString("de-CH")} kWp`,
                        sub: "empfohlen",
                      },
                      {
                        id: "yield",
                        label: "Jahresertrag",
                        value: `${estimate.yearlyYield.toLocaleString("de-CH")} kWh`,
                        sub: "pro Jahr",
                      },
                      {
                        id: "savings",
                        label: "Ersparnis",
                        value: `CHF ${estimate.savings.toLocaleString("de-CH")}`,
                        sub: "pro Jahr",
                      },
                      {
                        id: "payback",
                        label: "Amortisation",
                        value: estimate.payback
                          ? `${estimate.payback.toLocaleString("de-CH")} J.`
                          : "—",
                        sub: "geschätzt",
                      },
                    ].map((m) => (
                      <div
                        key={m.id}
                        data-testid={`funnel-estimate-${m.id}`}
                        className="bg-[var(--sol-surface-2)] border border-white/8 rounded-2xl p-5 md:p-6 text-center hover:border-[var(--sol-bronze)]/40 transition-colors"
                      >
                        <p className="text-[10px] tracking-[0.22em] uppercase text-white/40 font-body">
                          {m.label}
                        </p>
                        <p className="font-display text-2xl md:text-3xl mt-3 bronze-metallic font-light">
                          {m.value}
                        </p>
                        <p className="text-[11px] text-white/40 mt-2 font-body">
                          {m.sub}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 p-5 md:p-6 rounded-2xl border border-white/8 bg-white/[0.02]">
                    <div className="flex flex-col md:flex-row gap-4 md:gap-8 md:items-center">
                      <div className="flex-1">
                        <p className="text-[11px] tracking-[0.22em] uppercase text-white/40 font-body">
                          Geschätzte Investition
                        </p>
                        <p className="font-display text-xl md:text-2xl text-white font-light mt-2">
                          CHF {estimate.invest.toLocaleString("de-CH")}
                        </p>
                      </div>
                      <div className="flex-1">
                        <p className="text-[11px] tracking-[0.22em] uppercase text-white/40 font-body">
                          Eigenverbrauchs­quote
                        </p>
                        <p className="font-display text-xl md:text-2xl text-white font-light mt-2">
                          ca. {estimate.selfUsePct}%
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col-reverse sm:flex-row items-center justify-between gap-4 mt-10">
                    <button
                      type="button"
                      onClick={back}
                      data-testid="funnel-back-estimate"
                      className="text-white/60 hover:text-white text-sm inline-flex items-center gap-2"
                    >
                      <ArrowLeft size={16} /> Zurück
                    </button>
                    <button
                      type="button"
                      onClick={() => setStep((s) => s + 1)}
                      data-testid="funnel-estimate-next"
                      className="btn-bronze rounded-full px-8 py-4 inline-flex items-center gap-2 text-sm font-medium"
                    >
                      Detaillierte Offerte anfordern
                      <ArrowRight size={16} />
                    </button>
                  </div>
                </motion.div>
              ) : isContactStep ? (
                <motion.form
                  key="contact-step"
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.4 }}
                  onSubmit={submit}
                  data-testid="funnel-contact-step"
                  className="space-y-6"
                >
                  <div>
                    <h3 className="font-display text-2xl md:text-3xl text-white font-light">
                      Wohin dürfen wir Ihre Solar-Analyse senden?
                    </h3>
                    <p className="font-body text-white/55 mt-3">
                      Wir kontaktieren Sie ausschliesslich zu Ihrer Anfrage. Keine Werbung.
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-x-8 gap-y-2">
                    <div>
                      <label className="text-[11px] tracking-[0.22em] uppercase text-white/40">
                        Name *
                      </label>
                      <input
                        data-testid="funnel-input-name"
                        required
                        className="sol-input"
                        value={contact.name}
                        onChange={(e) =>
                          setContact({ ...contact, name: e.target.value })
                        }
                        placeholder="Max Muster"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] tracking-[0.22em] uppercase text-white/40">
                        E-Mail *
                      </label>
                      <input
                        data-testid="funnel-input-email"
                        type="email"
                        required
                        className="sol-input"
                        value={contact.email}
                        onChange={(e) =>
                          setContact({ ...contact, email: e.target.value })
                        }
                        placeholder="max@beispiel.ch"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] tracking-[0.22em] uppercase text-white/40">
                        Telefon
                      </label>
                      <input
                        data-testid="funnel-input-phone"
                        className="sol-input"
                        value={contact.phone}
                        onChange={(e) =>
                          setContact({ ...contact, phone: e.target.value })
                        }
                        placeholder="+41 79 000 00 00"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="col-span-1">
                        <label className="text-[11px] tracking-[0.22em] uppercase text-white/40">
                          PLZ
                        </label>
                        <input
                          data-testid="funnel-input-plz"
                          className="sol-input"
                          value={contact.plz}
                          onChange={(e) =>
                            setContact({ ...contact, plz: e.target.value })
                          }
                          placeholder="6300"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="text-[11px] tracking-[0.22em] uppercase text-white/40">
                          Ort
                        </label>
                        <input
                          data-testid="funnel-input-ort"
                          className="sol-input"
                          value={contact.ort}
                          onChange={(e) =>
                            setContact({ ...contact, ort: e.target.value })
                          }
                          placeholder="Zug"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col-reverse sm:flex-row items-center justify-between gap-4 pt-6">
                    <button
                      type="button"
                      onClick={back}
                      data-testid="funnel-back-contact"
                      className="text-white/60 hover:text-white text-sm inline-flex items-center gap-2"
                    >
                      <ArrowLeft size={16} /> Zurück
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="funnel-submit"
                      className="btn-bronze rounded-full px-8 py-4 inline-flex items-center gap-2 text-sm font-medium disabled:opacity-60"
                    >
                      {submitting ? "Wird gesendet…" : "Solar-Analyse anfordern"}
                      {!submitting && <ArrowRight size={16} />}
                    </button>
                  </div>
                </motion.form>
              ) : (
                <motion.div
                  key={`step-${step}`}
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  transition={{ duration: 0.4 }}
                  data-testid={`funnel-step-${currentStep.key}`}
                >
                  <h3 className="font-display text-2xl md:text-3xl text-white font-light leading-tight">
                    {currentStep.title}
                  </h3>
                  <p className="font-body text-white/55 mt-3 text-sm md:text-base">
                    {currentStep.sub}
                  </p>

                  <div
                    className={`grid mt-8 gap-3 md:gap-4 ${
                      currentStep.cols === 3
                        ? "grid-cols-1 md:grid-cols-3"
                        : "grid-cols-1 md:grid-cols-2"
                    }`}
                  >
                    {currentStep.options.map((o) => {
                      const Icon = o.icon;
                      const active = answers[currentStep.key] === o.value;
                      return (
                        <button
                          key={o.value}
                          type="button"
                          data-testid={`funnel-option-${currentStep.key}-${o.value}`}
                          onClick={() => select(currentStep.key, o.value)}
                          className={`relative text-left p-5 md:p-6 rounded-2xl border transition-all duration-300 flex items-center gap-4 group ${
                            active
                              ? "border-[var(--sol-bronze)] bg-[var(--sol-bronze)]/10"
                              : "border-white/10 hover:border-[var(--sol-bronze)]/60 hover:bg-white/5"
                          }`}
                        >
                          {Icon && (
                            <div
                              className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${
                                active
                                  ? "bg-[var(--sol-bronze)]/20 text-[var(--sol-bronze-light)]"
                                  : "bg-white/5 text-white/70 group-hover:text-[var(--sol-bronze-light)]"
                              }`}
                            >
                              <Icon size={18} strokeWidth={1.5} />
                            </div>
                          )}
                          <span className="font-body text-white text-sm md:text-base">
                            {o.label}
                          </span>
                          {active && (
                            <span className="ml-auto text-[var(--sol-bronze)]">
                              <Check size={18} />
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>

                  <div className="flex items-center justify-between mt-10">
                    <button
                      type="button"
                      onClick={back}
                      disabled={step === 0}
                      data-testid="funnel-back"
                      className="text-white/60 hover:text-white text-sm inline-flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ArrowLeft size={16} /> Zurück
                    </button>
                    <span className="text-white/30 text-xs font-body">
                      Frage {step + 1} von {stepsConfig.length}
                    </span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
